import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  
  CardContent,
  CardMedia,
  Chip,
  Link,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { Email, Phone, Person, ExpandMore, ExpandLess } from '@mui/icons-material';
import apiClient from '../../../services/apiClient';

interface Artist {
  _id: string;
  artistName: string;
  mainImage: string;
  genres: string[];
  about: string;
  youtubeEmbeds: string[];
  spotifyEmbeds: string[];
  soundcloudEmbeds: string[];
  bandcampEmbeds: string[];
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  performanceLink?: string;
}

interface ArtistBlockProps {
  block: {
    heading: string;
    subheading?: string;
    artistIds: string[];
    layout: 'grid' | 'list';
  };
  theme?: any;
}

const ArtistBlock: React.FC<ArtistBlockProps> = ({ block, theme }) => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Safe theme access with fallbacks
  const safeTheme = {
    palette: {
      primary: { main: theme?.palette?.primary?.main || '#3A9188', contrastText: theme?.palette?.primary?.contrastText || '#ffffff' },
      background: { paper: theme?.palette?.background?.paper || '#ffffff' },
      text: { primary: theme?.palette?.text?.primary || '#000000', secondary: theme?.palette?.text?.secondary || '#666666' },
      divider: theme?.palette?.divider || '#e0e0e0'
    }
  };

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        setLoading(true);
        
        if (!block?.artistIds || block.artistIds.length === 0) {
          setLoading(false);
          return;
        }
        
        const data = await apiClient.post<Artist[]>('/api/bandrecommendations/batch', { 
          ids: block.artistIds 
        });
        setArtists(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching artists:', err);
        setError('Unable to load artists');
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, [block?.artistIds]);

  const toggleExpanded = (artistId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(artistId)) {
        newSet.delete(artistId);
      } else {
        newSet.add(artistId);
      }
      return newSet;
    });
  };

  const extractEmbedSrc = (embedCode: string): string | null => {
    const srcMatch = embedCode.match(/src="([^"]+)"/);
    return srcMatch ? srcMatch[1] : null;
  };

  const renderEmbed = (embedCode: string, index: number) => {
    const src = extractEmbedSrc(embedCode);
    if (!src) return null;

    return (
      <Box key={index} sx={{ mb: 2 }}>
        <iframe
          src={src}
          width="100%"
          height="315"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '8px' }}
        />
      </Box>
    );
  };

  const renderBandcampLink = (embedCode: string, index: number) => {
    const hrefMatch = embedCode.match(/href="([^"]+)"/);
    const href = hrefMatch ? hrefMatch[1] : null;
    
    if (!href) return null;

    return (
      <Box key={index} sx={{ mb: 2 }}>
        <Link 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          sx={{ color: safeTheme.palette.primary.main }}
        >
          Listen on Bandcamp
        </Link>
      </Box>
    );
  };

  const hasExpandableContent = (artist: Artist) => {
    return (
      (artist.youtubeEmbeds && artist.youtubeEmbeds.length > 0) ||
      (artist.spotifyEmbeds && artist.spotifyEmbeds.length > 0) ||
      (artist.soundcloudEmbeds && artist.soundcloudEmbeds.length > 0) ||
      (artist.bandcampEmbeds && artist.bandcampEmbeds.length > 0) ||
      artist.performanceLink ||
      artist.about?.length > 200
    );
  };

  const renderArtistCard = (artist: Artist) => {
    const isExpanded = expandedCards.has(artist._id);
    const showExpandButton = hasExpandableContent(artist);

    return (
      <Card
        key={artist._id}
        sx={{
          backgroundColor: safeTheme.palette.background.paper,
          color: safeTheme.palette.text.primary,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {artist.mainImage && (
          <CardMedia
            component="img"
            height="300"
            image={artist.mainImage}
            alt={artist.artistName}
            sx={{ objectFit: 'cover' }}
          />
        )}
        
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" gutterBottom sx={{ color: safeTheme.palette.primary.main }}>
            {artist.artistName}
          </Typography>

          {artist.genres && artist.genres.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {artist.genres.map((genre, idx) => (
                <Chip
                  key={idx}
                  label={genre}
                  size="small"
                  sx={{
                    backgroundColor: safeTheme.palette.primary.main,
                    color: safeTheme.palette.primary.contrastText
                  }}
                />
              ))}
            </Box>
          )}

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              whiteSpace: 'pre-wrap',
              color: safeTheme.palette.text.primary,
              ...(!isExpanded && artist.about?.length > 200 && {
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              })
            }}
          >
            {artist.about}
          </Typography>

          {isExpanded && (
            <>
              {artist.performanceLink && (
                <Box sx={{ mb: 2 }}>
                  <Link
                    href={artist.performanceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: safeTheme.palette.primary.main }}
                  >
                    View Performances
                  </Link>
                </Box>
              )}

              {artist.youtubeEmbeds && artist.youtubeEmbeds.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Videos
                  </Typography>
                  {artist.youtubeEmbeds.map((embed, idx) => renderEmbed(embed, idx))}
                </Box>
              )}

              {artist.spotifyEmbeds && artist.spotifyEmbeds.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Listen on Spotify
                  </Typography>
                  {artist.spotifyEmbeds.map((embed, idx) => renderEmbed(embed, idx))}
                </Box>
              )}

              {artist.soundcloudEmbeds && artist.soundcloudEmbeds.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Listen on Soundcloud
                  </Typography>
                  {artist.soundcloudEmbeds.map((embed, idx) => renderEmbed(embed, idx))}
                </Box>
              )}

              {artist.bandcampEmbeds && artist.bandcampEmbeds.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Bandcamp
                  </Typography>
                  {artist.bandcampEmbeds.map((embed, idx) => renderBandcampLink(embed, idx))}
                </Box>
              )}x
            </>
          )}

          {showExpandButton && (
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button
                fullWidth
                onClick={() => toggleExpanded(artist._id)}
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  color: safeTheme.palette.primary.main,
                  textTransform: 'none'
                }}
              >
                {isExpanded ? 'Show Less' : 'Show More'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!artists || artists.length === 0) {
    return null;
  }

  return (
    <Box sx={{ py: 8 }}>
      <Typography
        variant="h3"
        gutterBottom
        sx={{ 
          color: safeTheme.palette.primary.main,
          textAlign: 'center',
          mb: 2
        }}
      >
        {block.heading}
      </Typography>

      {block.subheading && (
        <Typography
          variant="h6"
          sx={{
            color: safeTheme.palette.text.secondary,
            textAlign: 'center',
            mb: 6
          }}
        >
          {block.subheading}
        </Typography>
      )}

      <Grid 
        container 
        spacing={4}
        sx={{
          ...(block.layout === 'list' && {
            flexDirection: 'column'
          })
        }}
      >
        {artists.map((artist) => (
          <Grid 
            item 
            key={artist._id}
            xs={12}
            md={block.layout === 'grid' ? 6 : 12}
            lg={block.layout === 'grid' ? 4 : 12}
          >
            {renderArtistCard(artist)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ArtistBlock;